import { getReviewId } from './Review';
import {
  Criteria,
  CriteriaFramework,
} from 'concepts/Criteria/framework/CriteriaFramework';
import { CRITERIA, tokenizeRoute } from 'query/routes';
import { getCsrfToken } from 'query';
import { NonRetriableError } from 'query/errors';

export type CriteriaResponse = {
  criteria: Criteria;
  framework?: CriteriaFramework;
  version?: string;
  protocolLink?: string;
};

// The criteria endpoints speak snake_case (protocol_link, possibly null);
// everything above the query layer uses camelCase.
type RawCriteriaResponse = Omit<
  CriteriaResponse,
  'protocolLink' | 'framework'
> & {
  framework?: CriteriaFramework | null;
  protocol_link?: string | null;
  message?: string;
};

const parseCriteriaResponse = (
  body: RawCriteriaResponse
): CriteriaResponse => ({
  criteria: body.criteria,
  // The server sends null for "no framework"; normalise here so no null
  // reaches the layers above - a null echoed into a save is rejected by
  // the schema.
  framework: body.framework ?? undefined,
  version: body.version,
  // null means the server has no link stored.
  protocolLink: body.protocol_link ?? '',
});

// Thrown when a save is rejected because the criteria changed on the server
// since the version the client last read (HTTP 409). Carries the current
// server state so the caller can merge the user's edits into it (PIER-882).
export class CriteriaConflictError extends Error {
  readonly criteria: Criteria;
  readonly framework?: CriteriaFramework;
  readonly version?: string;
  readonly protocolLink?: string;

  constructor(body: RawCriteriaResponse) {
    super(body.message || 'Criteria were updated elsewhere');
    this.name = 'CriteriaConflictError';
    const response = parseCriteriaResponse(body);
    this.criteria = response.criteria;
    this.framework = response.framework;
    this.version = response.version;
    this.protocolLink = response.protocolLink;
    // Restore the prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, CriteriaConflictError.prototype);
  }
}

// Thrown when the server rejects the save with field-level validation errors
// (HTTP 422 with an errors object), e.g. an invalid protocol link. Nothing
// was saved. A 422 without field errors falls through to the generic error.
export class CriteriaValidationError extends Error {
  readonly fieldErrors: Record<string, string[]>;

  constructor(body: { message?: string; errors: Record<string, string[]> }) {
    super(body.message || 'Criteria validation failed');
    this.name = 'CriteriaValidationError';
    this.fieldErrors = body.errors;
    Object.setPrototypeOf(this, CriteriaValidationError.prototype);
  }
}

// The session ended or the user signed out. NonRetriable, so the query client
// surfaces it immediately; the breakout window blanks itself on it, and a save
// turns it into a session-ended toast.
export class CriteriaAuthError extends NonRetriableError {
  constructor() {
    super('401', 'Not signed in');
    this.name = 'CriteriaAuthError';
    Object.setPrototypeOf(this, CriteriaAuthError.prototype);
  }
}

// A redirect only counts as an auth failure when it landed on sign-in, so
// benign redirects (proxy, canonicalisation) don't masquerade as sign-outs.
const isAuthFailure = (res: Response) =>
  res.status === 401 ||
  (res.redirected && new URL(res.url).pathname.startsWith('/sign_in'));

/**
 * Gets a list of review criteria and their details for a given review framework.
 * If framework is saved, it will be returned, otherwise it will be undefined.
 */
export const getCriteria = async (): Promise<CriteriaResponse> => {
  const reviewId = getReviewId();
  if (!reviewId)
    throw new Error("Couldn't get Review ID. Is this part of a review?");

  const url = tokenizeRoute(CRITERIA, {
    review_id: reviewId,
  });

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (isAuthFailure(res)) {
    throw new CriteriaAuthError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || body?.message || 'Failed to load criteria');
  }

  return parseCriteriaResponse(await res.json());
};

type UpdateCriteriaOptions = {
  framework?: CriteriaFramework;
  protocolLink?: string;
  // The version the client last read, so the server can detect concurrent
  // edits and reject with a 409 (surfaced as CriteriaConflictError).
  version?: string;
};

export const updateCriteria = async (
  criteria: Criteria,
  { framework, protocolLink, version }: UpdateCriteriaOptions = {}
): Promise<CriteriaResponse> => {
  const reviewId = getReviewId();
  if (!reviewId)
    throw new Error("Couldn't get Review ID. Is this part of a review?");

  const url = tokenizeRoute(CRITERIA, {
    review_id: reviewId,
  });

  const res = await fetch(url.toString(), {
    method: 'POST',
    body: JSON.stringify({
      criteria,
      framework,
      ...(version !== undefined ? { version } : {}),
      ...(protocolLink !== undefined ? { protocol_link: protocolLink } : {}),
    }),
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  });

  if (isAuthFailure(res)) {
    throw new CriteriaAuthError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));

    if (res.status === 409) {
      throw new CriteriaConflictError(body);
    }

    if (res.status === 422 && body?.errors) {
      throw new CriteriaValidationError(body);
    }

    throw new Error(body?.error || body?.message || 'Failed to save criteria');
  }

  return parseCriteriaResponse(await res.json());
};
